import type { InspectionTemplate } from 'inspection-types';

import { sortSections } from '../model/templateProjection';
import type { CustomizationState } from '../state/customizationState';
import {
  createSectionsRailViewModel,
  type SectionsRailViewModel,
} from './createSectionsRailViewModel';
import {
  createSectionViewModel,
  type SectionViewModel,
} from './createSectionViewModel';
import {
  createTemplateHeaderViewModel,
  type TemplateHeaderViewModel,
} from './createTemplateHeaderViewModel';
import { memoizeByNode } from './memoizeByNode';
import { LABEL_KEYS, type LabelKey, type Message } from './messages';

export type BreadcrumbViewModel = {
  key: LabelKey;
  isCurrent: boolean;
};

export type TemplateCustomizationViewModel = {
  breadcrumbs: BreadcrumbViewModel[];
  header: TemplateHeaderViewModel;
  rail: SectionsRailViewModel;
  sections: SectionViewModel[];
  emptyMessage: Message | null;
};

export type TemplateCustomizationInput = {
  template: InspectionTemplate;
  expandedBySectionId: CustomizationState['expandedBySectionId'];
};

export const BREADCRUMBS: BreadcrumbViewModel[] = [
  { key: LABEL_KEYS.breadcrumbLibrary, isCurrent: false },
  { key: LABEL_KEYS.breadcrumbEditTemplate, isCurrent: true },
];

const createMemoizedSection = memoizeByNode(createSectionViewModel);

export function createTemplateCustomizationViewModel({
  template,
  expandedBySectionId,
}: TemplateCustomizationInput): TemplateCustomizationViewModel {
  const sortedSections = sortSections(template.sections);

  return {
    breadcrumbs: BREADCRUMBS,
    header: createTemplateHeaderViewModel(template),
    rail: createSectionsRailViewModel(sortedSections),
    sections: sortedSections.map(section =>
      createMemoizedSection(section, expandedBySectionId[section.id] ?? false)
    ),
    emptyMessage:
      sortedSections.length === 0 ? { key: LABEL_KEYS.noSections } : null,
  };
}
